const WAVE_SELECTOR = '[data-bf-wave]';
const TEXT_SELECTOR = '[data-bf-wave-text]';
const BG_SELECTOR = '[data-bf-wave-bg]';
const SLOGAN_SELECTOR = '[data-bf-wave-slogan]';
const BG_ANIMATION_CLASS = 'animate-bfWaveUp';
const BG_ANIMATION_COLLECTION_CLASS = 'animate-bfWaveUpCollection';
const BG_ANIMATION_MOBILE_CLASS = 'animate-bfWaveUpMobile';
const BG_ANIMATION_COLLECTION_MOBILE_CLASS = 'animate-bfWaveUpCollectionMobile';
const TEXT_SLIDE_ANIMATION_CLASS = 'animate-bfWaveTextSlide';

const isMobile = () => window.innerWidth < 768; // md breakpoint
const SLOGAN_FADE_IN_CLASS = 'animate-fadeIn';
const SLOGAN_FADE_OUT_CLASS = 'animate-fadeOut';

const SLOGANS = ['BIS ZU 50% REDUZIERT', 'DIE BESTEN ANGEBOTE'];

const initializedWaves = new WeakSet<Element>();

const restartAnimation = (element: Element | null, animationClass: string) => {
  if (!element) return;
  element.classList.remove(animationClass);
  // Force reflow to restart the animation
  // eslint-disable-next-line @typescript-eslint/no-unused-expressions
  (element as HTMLElement).offsetWidth;
  element.classList.add(animationClass);
};

const startSloganCycling = (sloganElement: HTMLElement, startIndex: number = 1) => {
  let currentSloganIndex = startIndex;
  const targetIndex = SLOGANS.length - 1; // Final slogan index (MEHR FAMILIE)
  
  const cycleSlogan = () => {
    // Stop if we've reached the final slogan
    if (currentSloganIndex >= targetIndex) {
      return;
    }
    
    sloganElement.classList.remove(SLOGAN_FADE_IN_CLASS);
    sloganElement.classList.add(SLOGAN_FADE_OUT_CLASS);

    const handleFadeOut = () => {
      sloganElement.classList.remove(SLOGAN_FADE_OUT_CLASS);
      currentSloganIndex = currentSloganIndex + 1;
      
      sloganElement.textContent = SLOGANS[currentSloganIndex];
      
      // Set color based on current slogan
      // White for MEHR RABATTE (on black background)
      // Black for MEHR ZEIT (on beige wave background)
      // Black for MEHR FAMILIE (on beige wave background for better contrast)
      if (currentSloganIndex === 0) {
        sloganElement.style.color = '#FFFFFF';
      } else {
        // Use black for both MEHR ZEIT and MEHR FAMILIE for better contrast on beige background
        sloganElement.style.color = '#000000';
      }
      
      sloganElement.classList.add(SLOGAN_FADE_IN_CLASS);
      sloganElement.removeEventListener('animationend', handleFadeOut);
    };

    sloganElement.addEventListener('animationend', handleFadeOut, { once: true });
  };

  // Start first cycle after 3 seconds
  setTimeout(cycleSlogan, 700);
};

const playWave = (wave: Element | null) => {
  if (!wave) return;
  const text = wave.querySelector(TEXT_SELECTOR) as HTMLElement;
  const bg = wave.querySelector(BG_SELECTOR) as HTMLElement;
  const slogan = wave.querySelector(SLOGAN_SELECTOR) as HTMLElement;
  
  // Ensure wave background starts hidden (at initial animation position)
  // Match the animation start point to avoid jumps
  if (bg) {
    const isCollection = wave.hasAttribute('data-bf-wave-collection');
    // Set initial position to match animation start (0% keyframe)
    // Collections start at -525%, others at -400%
    bg.style.bottom = isCollection ? '-525%' : '-400%';
  }
  
  // Show slogan immediately (white on black background)
  if (slogan) {
    slogan.style.color = '#F7F3ED';
    slogan.textContent = SLOGANS[0];
    slogan.classList.remove('opacity-0');
    slogan.classList.add(SLOGAN_FADE_IN_CLASS);
  }
  
  // Use collection-specific animation if on collection template
  const isCollection = wave.hasAttribute('data-bf-wave-collection');
  // Delay wave background animation: 1.5s for collections, 0.75s for others
  const initialDelay = isCollection ? 1500 : 750;
  
  setTimeout(() => {
    // Use mobile-specific animations on mobile devices
    const isMobileDevice = isMobile();
    let animationClass: string;
    if (isCollection) {
      animationClass = isMobileDevice ? BG_ANIMATION_COLLECTION_MOBILE_CLASS : BG_ANIMATION_COLLECTION_CLASS;
    } else {
      animationClass = isMobileDevice ? BG_ANIMATION_MOBILE_CLASS : BG_ANIMATION_CLASS;
    }
    restartAnimation(bg, animationClass);
    
    // Change slogan color to black when wave starts covering
    // Timing: collections use 900ms, others use 2500ms
    if (slogan) {
      const sloganChangeDelay = isCollection ? 900 : 2500;
      setTimeout(() => {
        slogan.style.color = '#544435';
        slogan.textContent = SLOGANS[1]; // MEHR ZEIT
        // Start cycling from index 1 (MEHR ZEIT)
        startSloganCycling(slogan, 1);
      }, sloganChangeDelay);
    }
    
    // After wave animation starts, start text slide-in
    // Timing: collections use 1.5s wave animation, others use 3.5s
    const textAnimationDelay = isCollection ? 400 : 1200; // Start text animation 0.4s into collection wave (1.5s total) or 1.2s into regular wave (3.5s total)
    setTimeout(() => {
      if (text) {
        // Remove initial opacity class (color is already set to #8D7661 in HTML)
        text.classList.remove('opacity-0');
        restartAnimation(text, TEXT_SLIDE_ANIMATION_CLASS);
      }
    }, textAnimationDelay);
  }, initialDelay); // Delay before wave starts (1.5s for collections, 0.75s for others)
};

const collectWaves = (scope: ParentNode | Element = document): Element[] => {
  if (scope instanceof Element && scope.matches(WAVE_SELECTOR)) {
    return [scope];
  }
  return Array.from(scope.querySelectorAll(WAVE_SELECTOR));
};

export const playBfWaveAnimations = (scope: ParentNode | Element = document) => {
  collectWaves(scope).forEach((wave) => playWave(wave));
};

export const initBfWaveAnimation = (scope: ParentNode | Element = document) => {
  collectWaves(scope).forEach((wave) => {
    if (initializedWaves.has(wave)) return;
    initializedWaves.add(wave);
    if (wave.closest('#cartOverlay')) return;
    requestAnimationFrame(() => playWave(wave));
  });
};

declare global {
  interface Window {
    bfWaveAnimations?: {
      play: (scope?: ParentNode | Element) => void;
      init: (scope?: ParentNode | Element) => void;
    };
  }
}

if (typeof window !== 'undefined') {
  window.bfWaveAnimations = {
    play: playBfWaveAnimations,
    init: initBfWaveAnimation,
  };
}

export default initBfWaveAnimation;

